import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Send, Loader2, Users, ShieldAlert } from 'lucide-react';

/**
 * Bulk-send WhatsApp dialog.
 * Props:
 *   open, onOpenChange
 *   recipients: [{ to, patient_name, ticket_id }]
 *   defaultTemplateId (optional)
 *   onSent (result) => void
 */
export default function WhatsAppBulkDialog({ open, onOpenChange, recipients = [], defaultTemplateId = 'lapsed_reengage', onSent }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(defaultTemplateId);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    api.get('/whatsapp/templates').then(r => {
      setTemplates(r.data.data || []);
      setEnabled(r.data.enabled !== false);
    }).catch(() => toast.error('Failed to load templates'));
  }, [open]);

  const withPhone = recipients.filter(r => (r.to || r.patient_mobile || '').trim());
  const previewCount = withPhone.length;

  const submit = async () => {
    if (!templateId) { toast.error('Choose a template'); return; }
    if (!previewCount) { toast.error('No recipients with a phone number'); return; }
    setSending(true);
    try {
      const payload = {
        template_id: templateId,
        parameters: {},
        recipients: withPhone.map(r => ({
          to: r.to || r.patient_mobile,
          patient_name: r.patient_name || r.name,
          ticket_id: r.ticket_id || r.id,
        })),
      };
      const r = await api.post('/whatsapp/send-bulk', payload);
      setResult(r.data);
      const { sent, failed, skipped } = r.data;
      if (sent > 0) toast.success(`Sent ${sent} · Failed ${failed || 0} · Skipped ${skipped || 0}`);
      else if (skipped > 0) toast.warning(`Karix disabled — ${skipped} skipped`);
      else toast.error(`Bulk send failed (${failed || 0})`);
      onSent && onSent(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk send failed');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="wa-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Users className="w-4 h-4 text-emerald-600" />
            Bulk WhatsApp — {previewCount} patient{previewCount === 1 ? '' : 's'}
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            Every recipient gets the same template; the patient&apos;s name auto-fills the first placeholder.
          </DialogDescription>
        </DialogHeader>

        {!enabled && (
          <div className="rounded border border-amber-300 bg-amber-50 text-amber-900 text-xs p-2 flex items-start gap-2">
            <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>Karix API key is not configured. Messages will be logged as <code>skipped</code>.</div>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Template</label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger className="h-9 mt-1" data-testid="wa-bulk-template"><SelectValue placeholder="Choose template" /></SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.template_id} value={t.template_id}>
                    {t.name} <span className="text-[10px] text-stone-400 ml-1">· {t.category}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-stone-500 mt-1">
              The <code>{'{{1}}'}</code> variable will be auto-filled with each patient&apos;s name.
            </div>
          </div>

          <div className="border border-stone-200 rounded max-h-56 overflow-auto hc-scrollbar">
            <table className="w-full text-xs">
              <thead className="bg-stone-50 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1.5 font-semibold">Patient</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Mobile</th>
                </tr>
              </thead>
              <tbody>
                {withPhone.slice(0, 100).map((r, i) => (
                  <tr key={i} className="border-t border-stone-100">
                    <td className="px-2 py-1">{r.patient_name || r.name || '—'}</td>
                    <td className="px-2 py-1 font-mono">{r.to || r.patient_mobile}</td>
                  </tr>
                ))}
                {withPhone.length > 100 && (
                  <tr><td colSpan={2} className="px-2 py-1 text-stone-500 italic">…and {withPhone.length - 100} more</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {result && (
            <div className="rounded border border-stone-200 bg-stone-50 p-2 text-xs" data-testid="wa-bulk-result">
              <div className="font-semibold mb-1">Result</div>
              <div>Sent: <b className="text-emerald-700">{result.sent}</b> · Failed: <b className="text-red-700">{result.failed}</b> · Skipped: <b className="text-amber-700">{result.skipped || 0}</b></div>
              {(result.errors || []).slice(0, 3).map((er, i) => (
                <div key={i} className="text-[11px] text-red-700 mt-1">{er.to}: {typeof er.err === 'string' ? er.err : JSON.stringify(er.err)}</div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={sending}>Close</Button>
          <Button onClick={submit} disabled={sending || !previewCount}
            className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-bulk-submit">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Send to {previewCount}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
